import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, Modal, Field, Button, inr } from "../components/UI";
import { Trophy, TrendingUp, IndianRupee, Sparkles, Target, Edit, Plus, Trash2, Award, Users } from "lucide-react";
import { toast } from "sonner";

function isoMonth() { return new Date().toISOString().slice(0, 7); }

const SlabEditor = ({ slabs, onChange }) => {
  const updateSlab = (i, key, val) => {
    const list = [...slabs];
    list[i] = { ...list[i], [key]: Number(val) };
    onChange(list);
  };
  return (
    <div className="space-y-2">
      {slabs.map((s, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`slab-${i}`}>
          <span className="text-xs">Above ₹</span>
          <input type="number" value={s.above || 0} onChange={(e) => updateSlab(i, "above", e.target.value)} className="input-base w-32 font-mono-num text-sm" data-testid={`slab-above-${i}`}/>
          <span className="text-xs">→ Pay</span>
          <input type="number" step="0.1" value={s.pct || 0} onChange={(e) => updateSlab(i, "pct", e.target.value)} className="input-base w-20 font-mono-num text-sm" data-testid={`slab-pct-${i}`}/>
          <span className="text-xs">%</span>
          <button type="button" onClick={() => onChange(slabs.filter((_, idx) => idx !== i))} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={12}/></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...slabs, { above: 0, pct: 0 }])} className="text-xs text-[#C1121F] font-semibold flex items-center gap-1" data-testid="add-slab-btn">
        <Plus size={12}/> Add slab
      </button>
    </div>
  );
};

export default function Incentives() {
  const { user } = useAuth();
  const [month, setMonth] = useState(isoMonth());
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState(null);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("my");
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get(`/incentives/my-stats?month=${month}`), api.get("/incentives/rules")];
      if (user?.role === "admin") reqs.push(api.get(`/incentives/team?month=${month}`));
      const res = await Promise.all(reqs);
      setStats(res[0].data); setRules(res[1].data);
      if (res[2]) setTeam(res[2].data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const openRuleEdit = (rtype) => {
    const r = rules[rtype];
    setRuleForm({
      type: rtype,
      base_pct: r.base_pct ?? 0,
      slabs: r.slabs || [],
    });
    setRuleOpen(true);
  };

  const saveRule = async (e) => {
    e.preventDefault();
    try {
      await api.put("/incentives/rules", {
        type: ruleForm.type,
        base_pct: Number(ruleForm.base_pct),
        slabs: ruleForm.slabs.filter((s) => s.above > 0 && s.pct > 0),
      });
      toast.success("Rule updated");
      setRuleOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !stats) return <div className="p-8 text-[#475569]">Loading…</div>;

  const ProgressBar = ({ current, next, color }) => {
    if (!next) return <div className="text-[10px] text-green-700 font-semibold mt-1">🏆 Top slab reached!</div>;
    const pct = Math.min(100, (current / next.target) * 100);
    return (
      <div className="mt-1">
        <div className="flex justify-between text-[10px] text-[#475569] mb-0.5">
          <span>Next slab @ {next.next_pct}%</span>
          <span className="font-mono-num">Need ₹{Math.round(next.remaining).toLocaleString("en-IN")} more</span>
        </div>
        <div className="h-1.5 bg-[#E5E7EB] rounded overflow-hidden">
          <div className={`h-full bg-${color}-500 transition-all`} style={{ width: `${pct}%` }}/>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="incentives-page">
      <PageHeader
        title="Incentives Dashboard"
        subtitle="Sales · Collection · Monthly slabs · Per-product bonuses"
        action={<input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-base w-auto" data-testid="month-picker"/>}
      />

      {user?.role === "admin" && (
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("my")} data-testid="tab-stats" className={`px-4 py-2 text-sm font-bold rounded ${tab === "my" ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569]"}`}>
            My Stats
          </button>
          <button onClick={() => setTab("team")} data-testid="tab-team" className={`px-4 py-2 text-sm font-bold rounded ${tab === "team" ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569]"}`}>
            Team Report
          </button>
          <button onClick={() => setTab("rules")} data-testid="tab-rules" className={`px-4 py-2 text-sm font-bold rounded ${tab === "rules" ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569]"}`}>
            Rules
          </button>
        </div>
      )}

      {tab === "my" && (
        <>
          {/* Grand Total Hero */}
          <div className="card-flat p-6 mb-5 bg-gradient-to-r from-[#C1121F] to-amber-600 text-white" data-testid="grand-total-card">
            <div className="flex items-center gap-2 mb-1">
              <Trophy size={18}/>
              <span className="text-xs uppercase tracking-wider font-bold opacity-90">Your Earnings · {month}</span>
            </div>
            <div className="font-display font-bold text-4xl font-mono-num">{inr(stats.grand_total_incentive)}</div>
            <div className="text-xs opacity-90 mt-1">Sales + Product Bonus + Collection Incentive</div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Sales */}
            <div className="card-flat p-5" data-testid="sales-card">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={16} className="text-blue-600"/>
                <h3 className="font-display font-bold">Sales · {stats.sales.order_count} orders</h3>
              </div>
              <div className="font-mono-num font-bold text-2xl text-[#0F172A]">{inr(stats.sales.total)}</div>
              <div className="text-xs text-[#475569] mt-1">Slab applied: <b>{stats.sales.slab_used}</b> @ {stats.sales.applied_pct}%</div>
              <div className="mt-3 p-3 bg-blue-50 rounded">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-blue-800 font-semibold">Base Incentive</span>
                  <span className="font-mono-num font-bold text-blue-900">{inr(stats.sales.incentive_amount)}</span>
                </div>
                {stats.sales.product_specific_bonus > 0 && (
                  <div className="flex justify-between items-baseline mt-1">
                    <span className="text-xs text-blue-800 font-semibold">+ Product Bonus</span>
                    <span className="font-mono-num font-bold text-blue-900">{inr(stats.sales.product_specific_bonus)}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-blue-200">
                  <span className="text-sm font-bold">Sales Earnings</span>
                  <span className="font-mono-num font-bold text-xl text-blue-900" data-testid="sales-earnings">{inr(stats.sales.total_incentive)}</span>
                </div>
                <ProgressBar current={stats.sales.total} next={stats.sales.next_slab} color="blue"/>
              </div>
            </div>

            {/* Collection */}
            <div className="card-flat p-5" data-testid="collection-card">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee size={16} className="text-green-600"/>
                <h3 className="font-display font-bold">Collection · {stats.collection.receipt_count} receipts</h3>
              </div>
              <div className="font-mono-num font-bold text-2xl text-[#0F172A]">{inr(stats.collection.total)}</div>
              <div className="text-xs text-[#475569] mt-1">Slab applied: <b>{stats.collection.slab_used}</b> @ {stats.collection.applied_pct}%</div>
              <div className="mt-3 p-3 bg-green-50 rounded">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-bold text-green-800">Collection Earnings</span>
                  <span className="font-mono-num font-bold text-xl text-green-900" data-testid="collection-earnings">{inr(stats.collection.incentive_amount)}</span>
                </div>
                <ProgressBar current={stats.collection.total} next={stats.collection.next_slab} color="green"/>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === "team" && team && (
        <div className="card-flat p-5" data-testid="team-report">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-blue-50 p-3 rounded">
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Team Sales</div>
              <div className="font-mono-num font-bold text-xl">{inr(team.totals.sales)}</div>
            </div>
            <div className="bg-green-50 p-3 rounded">
              <div className="text-[10px] uppercase tracking-wider font-bold text-green-700">Team Collection</div>
              <div className="font-mono-num font-bold text-xl">{inr(team.totals.collection)}</div>
            </div>
            <div className="bg-[#FEF2F2] p-3 rounded">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#C1121F]">Total Payout</div>
              <div className="font-mono-num font-bold text-xl text-[#C1121F]">{inr(team.totals.incentive_payout)}</div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-wider text-[#475569]">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Staff</th>
                  <th className="text-right p-2">Sales</th>
                  <th className="text-center p-2">Slab</th>
                  <th className="text-right p-2">Sales Inc.</th>
                  <th className="text-right p-2">Collection</th>
                  <th className="text-right p-2">Coll. Inc.</th>
                  <th className="text-right p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {team.rows.map((r, i) => (
                  <tr key={r.user_id} className="border-t border-[#E5E7EB]" data-testid={`team-row-${r.user_id}`}>
                    <td className="p-2 font-bold text-[#94A3B8]">{i + 1}{i === 0 && " 🥇"}{i === 1 && " 🥈"}{i === 2 && " 🥉"}</td>
                    <td className="p-2 font-semibold">{r.user_name} <span className="text-[10px] text-[#94A3B8]">({r.role})</span></td>
                    <td className="p-2 text-right font-mono-num">{inr(r.sales_total)}</td>
                    <td className="p-2 text-center text-[10px]">{r.sales_slab}</td>
                    <td className="p-2 text-right font-mono-num text-blue-700">{inr(r.sales_incentive)}</td>
                    <td className="p-2 text-right font-mono-num">{inr(r.collection_total)}</td>
                    <td className="p-2 text-right font-mono-num text-green-700">{inr(r.collection_incentive)}</td>
                    <td className="p-2 text-right font-mono-num font-bold text-[#C1121F]">{inr(r.total_incentive)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "rules" && rules && (
        <div className="space-y-4" data-testid="rules-section">
          {["sales", "collection"].map((rtype) => (
            <div key={rtype} className="card-flat p-5" data-testid={`rule-card-${rtype}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {rtype === "sales" ? <TrendingUp size={16} className="text-blue-600"/> : <IndianRupee size={16} className="text-green-600"/>}
                  <h3 className="font-display font-bold capitalize">{rtype} Incentive Rules</h3>
                </div>
                <Button variant="secondary" onClick={() => openRuleEdit(rtype)} data-testid={`edit-rule-${rtype}`}><Edit size={12}/> Edit</Button>
              </div>
              <div className="text-sm">
                <div>Base: <b className="font-mono-num">{rules[rtype].base_pct}%</b></div>
                {(rules[rtype].slabs || []).map((s, i) => (
                  <div key={i} className="text-xs text-[#475569] ml-3 mt-1">
                    • Above ₹{Number(s.above).toLocaleString("en-IN")} → <b>{s.pct}%</b>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            💡 Tip: Per-product incentive % can be set in Products → product card (overrides base rule, ADDED on top).
          </div>
        </div>
      )}

      <Modal open={ruleOpen} onClose={() => setRuleOpen(false)} title={`Edit ${ruleForm?.type} Rule`} maxWidth="max-w-xl">
        {ruleForm && (
          <form onSubmit={saveRule} className="space-y-4">
            <Field label="Base % (applies below first slab)" required>
              <input data-testid="base-pct-input" type="number" step="0.1" required value={ruleForm.base_pct} onChange={(e) => setRuleForm({...ruleForm, base_pct: e.target.value})} className="input-base font-mono-num w-32"/>
            </Field>
            <Field label="Monthly Slabs (incentive % increases above each threshold)">
              <SlabEditor slabs={ruleForm.slabs} onChange={(s) => setRuleForm({...ruleForm, slabs: s})}/>
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setRuleOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-rule-btn">Save Rule</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
